// The WIRE shape of one authored rule, family by family.
//
// THIS FILE EXISTS TO BE DELETED. It maps the editor's fields onto `RuleParams`
// in `challenge-contracts` arm for arm — the family tag, which editor field
// becomes which typed term, and which terms are mandatory — and NOTHING else. No
// labels, no categories, no capability answers: those live in `ruleRegistry.ts`
// today and come from the generated registry in D. The day D generates this
// table, this file is removed rather than edited, which is why it carries no
// second copy of anything the registry already says.
//
// PINNED TO THE GENERATED CONTRACT. The table's type is derived from the
// generated `RuleParams`: every row must name a family the platform declares,
// and every term a row names must be a field of that family's arm, so a term
// renamed or a family removed in Rust is a compile error here, not a refusal at
// save.
//
// NO AUTHORED DEFAULTS. A term the author did not choose is a term nobody sold,
// so an unset mandatory term is a SAVE REFUSAL here, in the same words the API
// answers with — never a value this file supplies on their behalf.

import type { Ladder, RuleParams } from '../wire';
import {
  RULE_REGISTRY, consequencesForTiming, countsRuleOccurrences, NEWS_ARMS, NEWS_UNAVAILABLE_NOTICE,
  type RuleBuilderState,
} from './ruleRegistry';

/** A family the platform declares: the tag of one `RuleParams` arm. */
export type RuleFamily = RuleParams['rule_type'];

/** The terms one family's arm takes, beside its tag. */
type TermOf<F extends RuleFamily> = Exclude<keyof Extract<RuleParams, { rule_type: F }>, 'rule_type'> & string;

/** How one family's typed parameters are built from the editor's fields. */
export interface FamilyWire<Term extends string = string> {
  /** The editor's `value`/`valueMode` pair, under this term's name. */
  money?: Term;
  /** The editor's `value` as a bare number, under this term's name. */
  scalar?: Term;
  /** The editor's `value`/`valueMode` pair as a STOP DISTANCE, under this
   *  term's name: price units or a percent of the entry price. A second number
   *  control beside the rule's own would be two places to type one distance. */
  distance?: Term;
  /** The schedule arm assembled from the fields the editor lays out flat. The
   *  author states a deadline and its own keys; the ARM is which deadline they
   *  picked, and it is built here rather than by a control that would have to
   *  know the tagged shape. */
  schedule?: Term;
  /** The `EventWindow` assembled from the four terms the editor lays out flat.
   *  Its own builder, for the same reason the schedule has one: the window is
   *  ONE term made of four, and a partial one is incomplete in exactly the way
   *  a missing basis is. */
  window?: Term;
  /** The scalar is a Rust `u32`: it travels as a JSON NUMBER. serde refuses the
   *  string "3" for a whole number, though it accepts one for a `Decimal`. */
  wholeNumber?: true;
  /** Every other term the family takes. All mandatory: the typed form has no
   *  optional parameters beyond the ones spelled `Option` in Rust, and none of
   *  the families below has one. */
  terms?: readonly Term[];
}

/** Every family the builder authors, in `RuleParams` order. A family the
 *  platform declares and the builder does not author has no row; a row for a
 *  family the platform does not declare, or a term its arm does not take, does
 *  not compile. */
const WIRE: { readonly [F in RuleFamily]?: FamilyWire<TermOf<F>> } = {
  max_daily_loss: {
    money: 'limit',
    terms: ['commission_inclusive', 'limit_basis', 'day_start_reference'],
  },
  max_total_drawdown: { money: 'limit', terms: ['commission_inclusive'] },
  max_trailing_drawdown: {
    money: 'limit',
    terms: [
      'commission_inclusive',
      'peak_basis',
      'update_mode',
      'threshold_basis',
      'lock_breach_level',
    ],
  },
  max_risk_per_trade: { money: 'limit', terms: ['reference_basis'] },
  max_total_unrealized_loss: { money: 'limit', terms: ['limit_basis'] },
  max_position_size: { scalar: 'max_lots' },
  max_concurrent_positions: { scalar: 'max_positions', wholeNumber: true },
  max_total_exposure: { scalar: 'max_quantity', terms: ['unit'] },
  max_leverage: { scalar: 'max_leverage' },
  trading_hours: { terms: ['start', 'end', 'timezone'] },
  weekend_holding: { terms: ['close_before_minutes', 'session_scope'] },
  flat_by_schedule: { schedule: 'schedule' },
  news_trading: { window: 'window', terms: ['arm'] },
  instrument_whitelist: { terms: ['symbols'] },
  instrument_blacklist: { terms: ['symbols'] },
  mandatory_stop_loss: {},
  stop_loss_required: {},
  max_stop_loss_distance: { distance: 'distance' },
  max_stop_target_ratio: { scalar: 'max_ratio' },
  max_intended_risk_per_trade: { money: 'limit' },
  max_aggregate_intended_risk: { money: 'limit' },
  max_daily_trades: { scalar: 'max_trades', wholeNumber: true },
};

/** The table, read by a family named at run time (an editor row, a stored rule). */
export const RULE_PARAMS_WIRE: Readonly<Record<string, FamilyWire | undefined>> = WIRE;

/** The money term, in the unit the author picked. */
function moneyLimit(rule: RuleBuilderState): Record<string, string> | null {
  const value = rule.value.trim();
  if (!value) return null;
  if (rule.valueMode === 'percent') return { percent: value };
  if (rule.valueMode === 'absolute') return { amount: value };
  return null;
}

/** The stop distance, in the unit the author picked. */
function priceDistance(rule: RuleBuilderState): Record<string, string> | null {
  const value = rule.value.trim();
  if (!value) return null;
  if (rule.valueMode === 'percent') return { percent_of_entry: value };
  if (rule.valueMode === 'absolute') return { price_units: value };
  return null;
}

/**
 * The tagged `FlatSchedule` the author's fields state, or `null` when one of
 * that arm's own fields is unset.
 *
 * The three arms read DIFFERENT keys, which is why the editor replaces the
 * whole param set when the deadline changes: a key the chosen arm never reads
 * is a term the compiler refuses.
 */
function flatSchedule(rule: RuleBuilderState): Record<string, unknown> | null {
  const params = rule.params;
  const lead = Number(params['close_before_minutes']);
  const tail = Number(params['blackout_after_minutes']);
  if (!Number.isFinite(lead) || !Number.isFinite(tail)) return null;
  switch (params['boundary']) {
    case 'daily_time': {
      const time = String(params['time'] ?? '').trim();
      const timezone = String(params['tz'] ?? '').trim();
      if (time === '' || timezone === '') return null;
      return {
        daily: {
          clock: { time, timezone },
          close_before_minutes: lead,
          blackout_after_minutes: tail,
        },
      };
    }
    case 'session_close': {
      const scope = params['session_scope'];
      const minClosed = Number(params['min_closed_minutes']);
      if (typeof scope !== 'string' || scope === '' || !Number.isFinite(minClosed)) return null;
      const cutoffTime = String(params['latest_cutoff_time'] ?? '').trim();
      const cutoffZone = String(params['latest_cutoff_tz'] ?? '').trim();
      // Both keys of the backstop move together: a time with no zone is a
      // cutoff nobody can place.
      if (cutoffTime !== '' && cutoffZone === '') return null;
      return {
        session: {
          session_scope: scope,
          latest_daily_cutoff:
            cutoffTime === '' ? null : { time: cutoffTime, timezone: cutoffZone },
          min_closed_minutes: minClosed,
          close_before_minutes: lead,
          blackout_after_minutes: tail,
        },
      };
    }
    case 'before_calendar_event': {
      const impacts = params['impacts'];
      const currencies = params['currencies'];
      if (!Array.isArray(impacts) || impacts.length === 0) return null;
      if (!Array.isArray(currencies) || currencies.length === 0) return null;
      // A DEADLINE IS PLANNED before any instrument is in hand, so the
      // instrument's own currencies name nothing to plan against: this arm
      // lists them.
      return {
        calendar: {
          impacts,
          currencies: { listed: currencies },
          before_minutes: lead,
          after_minutes: tail,
        },
      };
    }
    default:
      return null;
  }
}

/**
 * The `EventWindow` the author's four fields state, or `null` when one of them
 * is unset.
 *
 * ALL FOUR OR NONE. A window is what a rule measures against, and a term the
 * author did not choose is a blackout nobody sold: an editor that supplied
 * `currencies: 'instrument'` and two zero margins on their behalf wrote a rule
 * that judges only the event's own minute, under a currency scope nobody
 * picked. Both are terms a trader is held to.
 */
function eventWindow(rule: RuleBuilderState): Record<string, unknown> | null {
  const stated = rule.params['window'];
  if (typeof stated !== 'object' || stated === null) return null;
  const window = stated as Record<string, unknown>;
  const impacts = window['impacts'];
  if (!Array.isArray(impacts) || impacts.length === 0) return null;
  // THE DECODER'S OWN VOCABULARY, and nothing wider. `currencies` is the
  // instrument's own or an explicitly listed set, and no editor authors a
  // listed set — so any other string is a rule the server refuses with
  // `unknown variant`, and an incomplete rule is the honest reading of one the
  // author cannot state here.
  const currencies = window['currencies'];
  if (currencies !== 'instrument') return null;
  const before = Number(window['before_minutes']);
  const after = Number(window['after_minutes']);
  if (!Number.isFinite(before) || !Number.isFinite(after)) return null;
  return {
    impacts,
    currencies,
    before_minutes: before,
    after_minutes: after,
  };
}

/** Digits only, within `u32`: "3" → 3; "3.5", "-1", "" and "3e2" → null. */
function wholeNumber(raw: string): number | null {
  const text = raw.trim();
  if (!/^\d{1,10}$/.test(text)) return null;
  const value = Number(text);
  return value <= 4_294_967_295 ? value : null;
}

/**
 * Every mandatory term this rule has not been given a value for.
 *
 * The limit counts: a family that takes one and was left blank is incomplete in
 * exactly the same way a missing basis is.
 */
export function missingRuleTerms(rule: RuleBuilderState): string[] {
  const wire = RULE_PARAMS_WIRE[rule.ruleType];
  if (!wire) return [];
  const missing: string[] = [];
  if (wire.money && !moneyLimit(rule)) missing.push(wire.money);
  if (wire.scalar && (wire.wholeNumber ? wholeNumber(rule.value) === null : !rule.value.trim())) missing.push(wire.scalar);
  if (wire.distance && !priceDistance(rule)) missing.push(wire.distance);
  if (wire.schedule && !flatSchedule(rule)) missing.push(wire.schedule);
  if (wire.window && !eventWindow(rule)) missing.push(wire.window);
  for (const term of wire.terms ?? []) {
    const stated = rule.params[term];
    if (stated === undefined || stated === null || stated === '') missing.push(term);
  }
  return missing;
}

/** The refusal an incomplete rule earns, in the words the API answers with. */
export function ruleTermRefusal(ruleType: string, missing: string[]): string {
  return `${ruleType}: missing field \`${missing[0]}\``;
}

/**
 * The tagged parameters object this rule states.
 *
 * `null` when a mandatory term is unset — the caller refuses the save rather
 * than sending a rule the API would have to reject, and rather than filling the
 * gap with a number nobody authored.
 */
export function toRuleParams(rule: RuleBuilderState): RuleParams | null {
  const wire = RULE_PARAMS_WIRE[rule.ruleType];
  if (!wire || missingRuleTerms(rule).length > 0) return null;
  const params: Record<string, unknown> = { rule_type: rule.ruleType };
  const money = moneyLimit(rule);
  if (wire.money && money) params[wire.money] = money;
  if (wire.scalar) params[wire.scalar] = wire.wholeNumber ? wholeNumber(rule.value) : rule.value.trim();
  const distance = priceDistance(rule);
  if (wire.distance && distance) params[wire.distance] = distance;
  const schedule = flatSchedule(rule);
  if (wire.schedule && schedule) params[wire.schedule] = schedule;
  const window = eventWindow(rule);
  if (wire.window && window) params[wire.window] = window;
  for (const term of wire.terms ?? []) params[term] = rule.params[term];
  // The one place an editor row becomes the generated type: its keys are the
  // pinned table's, and its values are refused by name at the server when out
  // of their domain.
  return params as RuleParams;
}

/** Validate the whole authored ladder before any request leaves the editor. */
export function ruleAuthoringRefusal(rule: RuleBuilderState): string | null {
  const config = RULE_REGISTRY[rule.ruleType];
  if (!config?.implemented) return `${config?.label ?? rule.ruleType} cannot be published because its evaluation is unavailable.`;
  if (rule.ruleType === 'news_trading') {
    const arm = NEWS_ARMS.find((choice) => choice.value === rule.params.arm);
    if (rule.params.arm !== undefined && !arm) return NEWS_UNAVAILABLE_NOTICE;
    if (arm && rule.timing !== arm.timing) {
      return 'Choose the news arm again to set its evaluation timing.';
    }
  }
  const choices = consequencesForTiming(rule.timing, rule.ruleType);
  if (![rule.action, ...rule.repeats.map(([, action]) => action)]
    .every((action) => choices.some((choice) => choice.value === action))) {
    return 'Choose a consequence this rule can deliver for the first occurrence and every repeat.';
  }
  if (rule.repeats.length && !countsRuleOccurrences(rule.timing)) {
    return 'Repeats cannot be published for this rule because its evaluation does not count occurrences.';
  }
  if (rule.repeats.length > 32) return 'A rule can have at most 32 repeat rungs.';
  let previous = 1;
  for (const [from] of rule.repeats) {
    if (!Number.isInteger(from) || from <= previous || from > 4294967295) {
      return 'Repeat occurrences must be whole numbers from 2 to 4294967295, in strictly increasing order.';
    }
    previous = from;
  }
  const missing = missingRuleTerms(rule);
  return missing.length ? ruleTermRefusal(rule.ruleType, missing) : null;
}

/** Preserve every authored occurrence threshold and consequence. */
export function toLadder(rule: RuleBuilderState): Ladder {
  return { first: rule.action, repeats: rule.repeats } as Ladder;
}

/** The family a stored declaration names: its params object's own tag. */
export function ruleFamily(rule: { params?: { rule_type?: string } }): string {
  return rule.params?.rule_type ?? '';
}

/**
 * The authored number a stored declaration states, and its unit — for the
 * read-only cards that quote a limit back at a trader: a percent, an amount
 * of money (in the currency of the run it belongs to, which the card names),
 * or a family's own scalar term (lots, positions, leverage, trades, a ratio),
 * which is never money.
 *
 * `null` for a family whose terms are not a single number; those cards name the
 * rule rather than quoting one of its fields as though it were the whole of it.
 */
export function ruleAmount(rule: {
  params?: Record<string, unknown>;
}): { value: string; unit: 'percent' | 'money' } | { value: string; unit: 'scalar' } | null {
  const params = rule.params ?? {};
  const family = String(params['rule_type'] ?? '');
  const wire = RULE_PARAMS_WIRE[family];
  if (!wire) return null;
  if (wire.money) {
    const limit = params[wire.money] as Record<string, unknown> | undefined;
    if (!limit) return null;
    if (limit['percent'] != null) return { value: String(limit['percent']), unit: 'percent' };
    if (limit['amount'] != null) return { value: String(limit['amount']), unit: 'money' };
    return null;
  }
  if (wire.scalar && params[wire.scalar] != null) {
    return { value: String(params[wire.scalar]), unit: 'scalar' };
  }
  return null;
}
